import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { MoreHorizontal, Eye, Edit2, Trash2 } from 'lucide-react';
import DivisionForm from '../components/DivisionsComponent/DivisionForm';
import Pagination from '../helpers/Pagination';
import PageLoader from '../helpers/PageLoader';
import NoDataAvailable from '../helpers/NoDataAvailable';
import DeleteConfirmationOffcanvas from '../helpers/DeleteConfirmationOffcanvas';
import { useNavigate } from 'react-router-dom';
// import { useSelector } from 'react-redux';
import AccessDenied from '../helpers/AccessDenied';
import api from '../common/apiClient';
import SummaryApi from '../common/SummaryApi';

const Divisions = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [divisions, setDivisions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    selectedData: null,
    error: null,
  });
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  const token = localStorage.getItem('ata');
  
  // Permissions
//   const user = useSelector((state) => state?.user?.user);
//   const hasPermission = (permPath) =>
//     permPath?.split('.').reduce((obj, key) => obj?.[key], user) ?? false;

//   const canView = hasPermission('permissions.divisions.view') || user?.roleName === 'Super Admin';
//   const canAdd = hasPermission('permissions.divisions.add') || user?.roleName === 'Super Admin';
//   const canEdit = hasPermission('permissions.divisions.edit') || user?.roleName === 'Super Admin';
//   const canDelete = hasPermission('permissions.divisions.delete') || user?.roleName === 'Super Admin';
 const canView = true;
  const canAdd = true;
  const canEdit = true;
  const canDelete = true;

  // Configure api defaults
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Handle click outside and scroll for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuIndex(null);
      }
    };

    const handleScroll = () => {
      setMenuIndex(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  // Fetch divisions
  const fetchDivisions = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllDivisions.url);
      if (response.data.success) {
        setDivisions(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch divisions:', error);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  // Filter data based on search
  useEffect(() => {
    if (divisions.length > 0) {
      const query = searchTerm.toLowerCase();
      const result = divisions.filter((div) => {
        return (
          div?.divisionId?.toLowerCase()?.includes(query) ||
          div?.name?.toLowerCase()?.includes(query) ||
          div?.description?.toLowerCase()?.includes(query)
        );
      });
      setFilteredData(result);
    } else {
      setFilteredData([]);
    }
    setCurrentPage(1);
  }, [divisions, searchTerm]);

  // Handle successful create/update from form
  const handleFormSuccess = (data) => {
    // Refresh the divisions list
    fetchDivisions();
    // Close the form
    handleCloseForm();
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      const response = await api.delete(SummaryApi.deleteDivision.url(id));
      if (response.data.success) {
        setDivisions((prev) => prev.filter((div) => div._id !== id));
        toast.success('Division deleted successfully');
        setDeleteConfirmation({
          isOpen: false,
          selectedData: null,
          error: null,
        });
      }
    } catch (error) {
      console.error('Failed to delete division:', error);
      
      // Handle specific error with associated data
      if (error.response?.data?.success === false && error.response?.data?.data) {
        setDeleteConfirmation(prev => ({
          ...prev,
          error: {
            message: error.response.data.message,
            departments: error.response.data.data, // Associated departments
          }
        }));
      } else {
        toast.error(error.response?.data?.message || 'Error deleting division');
        setDeleteConfirmation({
          isOpen: false,
          selectedData: null,
          error: null,
        });
      }
    }
  };

  // Handle actions
  const handleView = (data) => {
    setSelectedDivision(data);
    setIsViewMode(true);
    setIsEditMode(false);
    setShowForm(true);
    setMenuIndex(null);
  };

  const handleEdit = (data) => {
    setSelectedDivision(data);
    setIsEditMode(true);
    setIsViewMode(false);
    setShowForm(true);
    setMenuIndex(null);
  };

  const handleAdd = () => {
    setSelectedDivision(null);
    setIsEditMode(false);
    setIsViewMode(false);
    setShowForm(true);
  };

  const openDeleteModal = (data) => {
    setDeleteConfirmation({
      isOpen: true,
      selectedData: data,
      error: null,
    });
    setMenuIndex(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setIsEditMode(false);
    setIsViewMode(false);
    setSelectedDivision(null);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  // Pagination calculations
  const totalItems = filteredData.length;
  const paginatedData = itemsPerPage === "All"
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  let serialNumber = 1;

  if (!canView) {
    return <AccessDenied />;
  }

  return (
    <div className="w-full p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBackClick}
            className="mr-2 hover:bg-gray-300 rounded-full p-1"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-xl md:text-2xl font-bold">Divisions Management</span>
        </div>
        
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          className={`bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
            !canAdd ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <FaPlus /> Add Division
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search divisions..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <PageLoader />
      ) : paginatedData.length === 0 ? (
        <NoDataAvailable />
      ) : (
        <div className="w-full overflow-x-auto custom-scrollbar">
          <div className="min-w-[800px] space-y-2">
            {/* Header Row */}
            <div className="grid grid-cols-[60px_1fr_2fr_2fr_1fr_80px] gap-x-5 text-sm font-semibold bg-red-50 text-gray-700 px-3 py-4 rounded">
              <div>S No</div>
              <div>Division ID</div>
              <div>Name</div>
              <div>Description</div>
              <div>Created Date</div>
              <div>Action</div>
            </div>

            {/* Data Rows */}
            {paginatedData.map((division, index) => (
              <div
                key={division._id}
                className="relative grid grid-cols-[60px_1fr_2fr_2fr_1fr_80px] gap-x-5 text-sm bg-white shadow rounded px-3 py-4 items-start"
              >
                <div>
                  {itemsPerPage !== "All"
                    ? currentPage * itemsPerPage - itemsPerPage + serialNumber++
                    : serialNumber++}
                </div>
                <div>{division.divisionId}</div>
                <div>{division.name}</div>
                <div className="line-clamp-2">{division.description || '-'}</div>
                <div>{new Date(division.createdAt).toLocaleDateString()}</div>
                <div className="flex gap-2 items-center">
                  <div className="relative inline-block text-left">
                    <button
                      onClick={(e) => {
                        const button = e.currentTarget;
                        const buttonRect = button.getBoundingClientRect();
                        setMenuIndex(
                          menuIndex === index
                            ? null
                            : {
                                index,
                                position: {
                                  top: buttonRect.bottom + window.scrollY,
                                  left: buttonRect.right - 144,
                                  direction:
                                    window.innerHeight - buttonRect.bottom > 150
                                      ? "below"
                                      : "above",
                                },
                              }
                        );
                      }}
                      className="hover:bg-gray-100 rounded-full p-1"
                    >
                      <MoreHorizontal size={20} />
                    </button>

                    {menuIndex?.index === index && (
                      <div
                        ref={dropdownRef}
                        className="fixed z-50 w-36 bg-white shadow-lg rounded-md border border-gray-200"
                        style={{
                          top:
                            menuIndex.position.direction === "below"
                              ? `${menuIndex.position.top}px`
                              : `${menuIndex.position.top - 144}px`,
                          left: `${menuIndex.position.left}px`,
                        }}
                      >
                        <ul className="text-sm divide-y divide-gray-200">
                          <li
                            className={`px-4 py-2 flex items-center gap-2 ${
                              canView
                                ? 'hover:bg-gray-50 cursor-pointer'
                                : 'cursor-not-allowed text-gray-400'
                            }`}
                            onClick={() => {
                              if (canView) {
                                handleView(division);
                              }
                            }}
                          >
                            <Eye size={16} /> View
                          </li>
                          <li
                            className={`px-4 py-2 flex items-center gap-2 ${
                              canEdit
                                ? 'hover:bg-gray-50 cursor-pointer'
                                : 'cursor-not-allowed text-gray-400'
                            }`}
                            onClick={() => {
                              if (canEdit) {
                                handleEdit(division);
                              }
                            }}
                          >
                            <Edit2 size={16} /> Edit
                          </li>
                          <li
                            className={`px-4 py-2 flex items-center gap-2 ${
                              canDelete
                                ? 'hover:bg-gray-50 cursor-pointer'
                                : 'cursor-not-allowed text-gray-400'
                            }`}
                            onClick={() => {
                              if (canDelete) {
                                openDeleteModal(division);
                              }
                            }}
                          >
                            <Trash2 size={16} /> Delete
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Division Form Offcanvas */}
      <DivisionForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        isEditMode={isEditMode}
        isViewMode={isViewMode}
        initialData={selectedDivision}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmationOffcanvas
        isOpen={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({
            isOpen: false,
            selectedData: null,
            error: null,
          })
        }
        onConfirm={() => handleDelete(deleteConfirmation.selectedData?._id)}
        title="Delete Division"
        itemName={deleteConfirmation.selectedData?.name}
        error={deleteConfirmation.error}
        onNavigateToItems={() => {
          setDeleteConfirmation({
            isOpen: false,
            selectedData: null,
            error: null,
          });
          navigate('/admin-dashboard/departments');
        }}
        navigateButtonText="Go to Departments"
      />
    </div>
  );
};

export default Divisions;
